import * as _ from 'lodash';
import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap';

import type { ButtonType } from './Button';
import Button, { ButtonGroup } from './Button';
import Icon, { icons } from './Icon';

type ConfirmModalProps = {
  isOpen: boolean;
  title: string;
  body: ReactNode;
  confirmText?: string;
  confirmBtnType?: ButtonType;
  cancelText?: string;
  onHide: () => void;
  onConfirm?: () => void | boolean;
  isNested?: boolean;
};

/**
 * The generic/global confirmation modal (ie: `window.confirm`)
 *
 * @param {ConfirmModalProps} props - ConfirmModal props
 * @param {boolean} props.isOpen - If the modal is currently opened or not.
 * @param {string} props.title - Modal heading.
 * @param {ReactNode} props.body - Modal body.
 * @param {string} [props.confirmText] - Text for the confirmation button. @default 'Confirm'.
 * @param {ButtonType} [props.confirmBtnType] - Type for the confirm button. @default 'primary'.
 * @param {string} [props.cancelText] - Text for the cancel button. @default 'Cancel'.
 * @param {() => void} props.onHide - Method for hiding the modal.
 * @param {() => void | boolean} [props.onConfirm] - What happens when the user clicks confirm.
 * @deprecated - Instead of rendering this component directly, invoke DialogContext.onConfirm programatically from your component.
 */
const ConfirmModal = ({
  isOpen,
  title,
  body,
  confirmText = 'Confirm',
  confirmBtnType = 'primary',
  cancelText = 'Cancel',
  onHide,
  onConfirm = _.noop,
  isNested = false,
}: ConfirmModalProps) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    setIsLoading(false);
  }, [isOpen]);

  const onConfirmClick = () => {
    setIsLoading(true);
    const isConfirmed = onConfirm();
    //  TODO: check if any of onConfirm actually returns false, if not remove this.
    if (isConfirmed === false) {
      throw new Error('Cannot proceed with confirm');
    }
    onHide();
    setIsLoading(false);
  };

  return (
    <Modal
      onHide={onHide}
      show={isOpen}
      className="modal-confirm"
      backdropClassName={isNested ? 'modal-nested' : undefined}
    >
      <Modal.Header closeButton>
        <Modal.Title>
          {title}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {body}
      </Modal.Body>
      <Modal.Footer>
        <ButtonGroup>
          <Button
            type="secondary"
            onClick={onHide}
          >
            {cancelText}
          </Button>
          <Button
            type={confirmBtnType}
            onClick={onConfirmClick}
            isDisabled={isLoading}
          >
            {isLoading && (
              <span className="margin-right-xs">
                <Icon icon={icons.circleO} hasSpin />
              </span>
            )}
            {confirmText}
          </Button>
        </ButtonGroup>
      </Modal.Footer>
    </Modal>
  );
};

export default ConfirmModal;
